import { useState, useEffect, useRef, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Collapse,
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import dayjs from "dayjs";

import request from "../../../utilities/requests";
import { toast } from "../../../utilities/SnackBarUtils";

const PAGE_SIZE = 50;

const HIDDEN_META_KEYS = new Set(["admin", "email", "event"]);

const Row = ({ entry }) => {
  const [open, setOpen] = useState(false);
  const meta = entry.meta || {};
  const detailEntries = Object.entries(meta).filter(
    ([k]) => !HIDDEN_META_KEYS.has(k)
  );
  const hasDetails = detailEntries.length > 0;

  return (
    <>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell sx={{ width: 40, p: 0.5 }}>
          {hasDetails && (
            <IconButton size="small" onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell sx={{ whiteSpace: "nowrap" }}>
          {entry.timestamp
            ? dayjs(entry.timestamp).local().format("MM/DD/YY hh:mm:ss A")
            : ""}
        </TableCell>
        <TableCell sx={{ whiteSpace: "nowrap" }}>{meta.email || "—"}</TableCell>
        <TableCell>{entry.message}</TableCell>
      </TableRow>
      {hasDetails && (
        <TableRow>
          <TableCell colSpan={4} sx={{ p: 0, border: 0 }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box
                sx={{
                  m: 1,
                  p: 1.5,
                  bgcolor: "grey.100",
                  borderRadius: 1,
                  maxHeight: 400,
                  overflow: "auto",
                }}
              >
                <pre
                  style={{
                    margin: 0,
                    fontSize: "0.8rem",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {JSON.stringify(Object.fromEntries(detailEntries), null, 2)}
                </pre>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const EventActivityLogDialog = ({ open, close, eventId }) => {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const scrollRef = useRef(null);
  const sentinelRef = useRef(null);
  // Refs guard the IntersectionObserver callback against stale closures
  // and prevent overlapping fetches while one is in flight.
  const loadingRef = useRef(false);
  const entriesLenRef = useRef(0);
  const totalRef = useRef(0);

  useEffect(() => {
    entriesLenRef.current = entries.length;
  }, [entries]);

  useEffect(() => {
    totalRef.current = total;
  }, [total]);

  const load = useCallback(
    async (skip, replace = false) => {
      if (loadingRef.current) return;
      loadingRef.current = true;
      if (skip === 0) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      try {
        const result = await request(
          `auditlog/event?eventId=${eventId}&limit=${PAGE_SIZE}&skip=${skip}`
        );
        if (result.success) {
          setEntries((prev) =>
            replace ? result.entries : [...prev, ...result.entries]
          );
          setTotal(result.total);
        } else {
          toast.error(result.error || "Failed to load activity log");
        }
      } catch (e) {
        toast.error(e?.message || "Failed to load activity log");
      } finally {
        loadingRef.current = false;
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [eventId]
  );

  useEffect(() => {
    if (open && eventId) {
      setEntries([]);
      setTotal(0);
      entriesLenRef.current = 0;
      totalRef.current = 0;
      load(0, true);
    }
  }, [open, eventId, load]);

  useEffect(() => {
    if (!open) return;
    const root = scrollRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel) return;

    const observer = new IntersectionObserver(
      (entriesObs) => {
        const entry = entriesObs[0];
        if (!entry?.isIntersecting) return;
        if (loadingRef.current) return;
        if (entriesLenRef.current >= totalRef.current) return;
        load(entriesLenRef.current);
      },
      { root, rootMargin: "200px 0px" }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [open, load, entries.length]);

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="lg">
      <DialogTitle>Event Activity Log</DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Box
          ref={scrollRef}
          sx={{
            maxHeight: "70vh",
            overflowY: "auto",
            px: 3,
            py: 2,
          }}
        >
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : entries.length === 0 ? (
            <DialogContentText>
              No admin activity logged for this event yet.
            </DialogContentText>
          ) : (
            <>
              <Typography
                variant="body2"
                sx={{ mb: 1, color: "text.secondary" }}
              >
                Showing {entries.length} of {total}
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell sx={{ fontWeight: "bold" }}>Time</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>User</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entries.map((entry, i) => (
                    <Row key={entry._id || i} entry={entry} />
                  ))}
                </TableBody>
              </Table>
              <Box
                ref={sentinelRef}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  py: 2,
                  minHeight: 32,
                }}
              >
                {loadingMore && <CircularProgress size={20} />}
              </Box>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EventActivityLogDialog;
